import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import i18n from 'i18n';
import styled from 'styled-components';

import { Form } from 'components';
import { FormWrapper } from 'components/Modal/styles';
import ModalForm from 'components/ModalForm/ModalForm';
import { useFetchRegionsSuperUserInfinite, useFetchRegionSuperUser } from 'api/regions/hooks';
import { useCreatePlan, useUpdatePlan } from 'api/plans/hooks';
import { currencies } from 'constants/currenciesList';

const SectionTitle = styled.h2`
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 8px;
  margin-bottom: 16px;
  font-size: 16px;
`;

const validationSchema = Yup.object({
  region: Yup.string().required(i18n.t('validation:required')),
  name: Yup.string().required(i18n.t('validation:required')),
  slug: Yup.string().required(i18n.t('validation:required')),
  price: Yup.number().required(i18n.t('validation:required')),
  currency: Yup.mixed()
    .oneOf(
      currencies.map(({ code }) => code),
      i18n.t('validation:currencyCodeNotValid'),
    )
    .required(i18n.t('validation:required')),
  max_locations: Yup.number().min(0).required(i18n.t('validation:required')),
  max_products: Yup.number().min(0).required(i18n.t('validation:required')),
  max_retailers: Yup.number().min(0).required(i18n.t('validation:required')),
  max_events: Yup.number().min(0).required(i18n.t('validation:required')),
});

const slugify = (str) =>
  str
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/(^-|-$)/g, '');

const SlugAutoFill = ({ regions, isEdit }) => {
  const { values, setFieldValue } = useFormikContext();

  useEffect(() => {
    if (isEdit || !values.region) return;
    const region = regions.find((r) => r.uid === values.region);
    if (region) setFieldValue('slug', slugify(`${region.name}-custom`));
  }, [values.region]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

const PlanForm = ({ type = 'create', initialValues, onSuccess }) => {
  const { t } = useTranslation(['pages', 'app']);
  const [regionSearch, setRegionSearch] = useState('');

  const {
    data: regionsData,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingRegions,
    isFetchingNextPage,
  } = useFetchRegionsSuperUserInfinite({ search: regionSearch });

  const { mutate: createPlan, isPending: isCreating } = useCreatePlan();
  const { mutate: updatePlan, isPending: isUpdating } = useUpdatePlan();

  const FormGroup = Form.GroupVertical;
  const isEdit = type === 'update';

  const { data: initialRegion } = useFetchRegionSuperUser(isEdit ? initialValues?.region : null);

  const regions = regionsData ? regionsData.pages.flatMap((p) => p.results) : [];
  const regionOptions = regions.map((r) => ({ value: r.uid, label: r.name }));

  const handleSubmit = (values) => {
    if (isEdit) {
      updatePlan({ slug: initialValues.slug, ...values }, { onSuccess });
    } else {
      createPlan(values, { onSuccess });
    }
  };

  return (
    <ModalForm
      initialValues={{
        name: '',
        slug: '',
        price: '',
        currency: '',
        max_locations: '',
        max_products: '',
        max_retailers: '',
        max_events: '',
        max_livestream_minutes: '',
        region: '',
        ...initialValues,
      }}
      onSubmit={handleSubmit}
      cancelLabel={t('app:cancel')}
      submitLabel={t(`app:${type}`)}
      validationSchema={validationSchema}
      isLoading={isCreating || isUpdating}
      pristine
    >
      <FormWrapper>
        <SlugAutoFill regions={regions} isEdit={isEdit} />

        <SectionTitle>{t('pages:superUser.sectionRegion')}</SectionTitle>
        <FormGroup required label={t('pages:superUser.region')}>
          <Form.MultiSelectInfinite
            options={regionOptions}
            name="region"
            isMulti={false}
            disabled={isEdit}
            hasMoreOptions={hasNextPage}
            loadMoreOptions={fetchNextPage}
            isLoadingOptions={isLoadingRegions}
            isLoadingMoreOptions={isFetchingNextPage}
            onSearch={setRegionSearch}
            initialOption={
              initialRegion ? { value: initialRegion.uid, label: initialRegion.name } : undefined
            }
          />
        </FormGroup>

        <SectionTitle>{t('pages:superUser.sectionPlanDetails')}</SectionTitle>
        <FormGroup required label={t('pages:superUser.planName')}>
          <Form.Input type="text" name="name" placeholder={t('pages:superUser.planName')} />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.slug')}>
          <Form.Input
            type="text"
            name="slug"
            placeholder="region-name-custom"
            disabled={isEdit}
            description={t('pages:superUser.slugDescription')}
          />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.price')}>
          <Form.Input type="number" name="price" placeholder="0.00" />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.currency')}>
          <Form.MultiSelect
            options={currencies.map((currency) => ({
              value: currency.code,
              label: currency.description,
            }))}
            name="currency"
            isMulti={false}
          />
        </FormGroup>

        <SectionTitle>{t('pages:superUser.sectionLimits')}</SectionTitle>
        <FormGroup required label={t('pages:superUser.maxLocations')}>
          <Form.Input type="number" name="max_locations" placeholder="0" min="0" />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.maxProducts')}>
          <Form.Input type="number" name="max_products" placeholder="0" min="0" />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.maxRetailers')}>
          <Form.Input type="number" name="max_retailers" placeholder="0" min="0" />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.maxEvents')}>
          <Form.Input type="number" name="max_events" placeholder="0" min="0" />
        </FormGroup>
        <FormGroup required label={t('pages:superUser.maxLivestreamMinutes')}>
          <Form.Input type="number" name="max_livestream_minutes" placeholder="0" min="0" />
        </FormGroup>
      </FormWrapper>
    </ModalForm>
  );
};

PlanForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  onSuccess: PropTypes.func,
};

export default PlanForm;
